import React, { useContext, useState, forwardRef, useImperativeHandle, useEffect } from "react";
import { TextField, DetailsList, SelectionMode, IColumn, Text } from "@fluentui/react";
import { css } from "@emotion/css";
import { getFormattedDateString } from "../../services/DateUtil";
import { SLCreateSLBatchContext } from "../../contexts/SLCreateSLBatchContext";
import { SLLoadingStatus } from "../../utilities/utilities";
import { ISLEntryResponse, CommentsResponse } from "./SLCreateSchemas";
import LoadingSpinner from "../../baseComponents/LoadingSpinner";

const style = css`
  .comments-onchange-style {
    margin-left: 50px;
  }
`;

const maxCommentsFieldLength = {
  newUserComment: 255
};

function commentsError(newUserComment: string) {
  const remaining = maxCommentsFieldLength.newUserComment - newUserComment.length;
  if (remaining < 0) {
    return `Comment exceeds max length. ${maxCommentsFieldLength.newUserComment} max characters`;
  }
  return `${remaining} characters remaining`;
}

export interface ISLCreateCommentFormRef {
  getNewComment: () => string;
  setComments: (theComments: CommentsResponse[]) => void;
  reset: () => void;
}

function CommentToComponent(item: CommentsResponse) {
  return function FormattedDate() {
    return <span>{getFormattedDateString(item.commentDate)}</span>;
  };
}

const SLCreateComments = forwardRef((props, ref) => {
  const entryContext = useContext(SLCreateSLBatchContext);
  const [loadingStatus, setLoadingStatus] = useState(SLLoadingStatus.Idle);
  const [value, setValue] = useState<string>("");
  const [comments, _setComments] = useState<CommentsResponse[]>([]);

  useImperativeHandle(ref, () => ({
    getNewComment() {
      return value;
    },
    setComments(theComments: CommentsResponse[]) {
      _setComments(theComments);
    },
    reset() {
      setValue("");
      _setComments([]);
    }
  }));

  useEffect(() => {
    if (entryContext.loadingStatus === SLLoadingStatus.Resolved) {
      if (entryContext.entry) {
        const entry = entryContext.entry as ISLEntryResponse;
        _setComments(entry.comments);
      } else {
        _setComments([]);
      }
      setLoadingStatus(SLLoadingStatus.Resolved);
    } else if (entryContext.loadingStatus === SLLoadingStatus.Rejected) {
      setLoadingStatus(SLLoadingStatus.Rejected);
    }
  }, [entryContext.loadingStatus, entryContext.entry]);

  const columns = [
    {
      key: "column1",
      name: "Role",
      fieldName: "persona",
      minWidth: 100,
      maxWidth: 200,
      isResizable: true
    },
    {
      key: "column2",
      name: "Date",
      fieldName: "commentDate",
      minWidth: 100,
      maxWidth: 200,
      isResizable: true,
      onRender: (item: CommentsResponse) => CommentToComponent(item)()
    } as IColumn,
    {
      key: "column3",
      name: "Comment",
      fieldName: "comments",
      minWidth: 120,
      maxWidth: 200,
      isResizable: true
    }
  ];

  if (loadingStatus === SLLoadingStatus.Pending) {
    return <LoadingSpinner label="Loading header" />;
  } else if (loadingStatus === SLLoadingStatus.Idle) {
    return null;
  } else if (loadingStatus === SLLoadingStatus.Rejected) {
    return (
      <Text
        variant="xLarge"
        style={{
          color: "var(--accent-font-color, gray)",
          textAlign: "center"
        }}
      >
        Error while loading Comments.
      </Text>
    );
  }

  return (
    <div className={style}>
      <div className="ms-Grid" dir="ltr">
        <div className="ms-Grid-col ms-lg6">
          <div className="comments-form">
            <DetailsList
              items={comments}
              columns={columns}
              selectionMode={SelectionMode.none}
              ariaLabel={"Comments table"}
              ariaLabelForGrid={"Table for user comments for SL Entry"}
            />
          </div>
        </div>
        <div className="ms-Grid-col ms-lg6">
          <div className="comments-onchange-style">
            <TextField
              name="newUserComment"
              label="Comments"
              value={value}
              onChange={(_, newValue) => setValue(newValue || "")}
              className="comments-text-style"
              description={commentsError(value)}
              multiline
              autoAdjustHeight
              rows={3}
              disabled={entryContext.isReadOnly}
            />
          </div>
        </div>
      </div>
    </div>
  );
});

SLCreateComments.displayName = "SLCreateComments";

export default SLCreateComments;
